import { RefreshCw, Shuffle } from "lucide-react";
import { useState } from "react";

import { useJDPreview, useJDQuestions } from "@/api/jd-question-queries";
import type { JDDetail } from "@/api/jd-queries";
import { MixLine, SourceChip } from "@/components/jd-source-chip";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Callout, Empty, ErrorNote, Panel, PanelHeader } from "@/components/ui/panel";
import { useDraft } from "@/lib/jd-draft-state";
import { bucketLabel, byBucket, countsFor } from "@/lib/jd-steps";

/**
 * A pick made the way ipp-platform makes one — DESIGN §20.9. `POST /jds/{id}/preview` runs the same code
 * as `/pick`, with the Interview mix's numbers (unsaved ones too), so what a member would be asked can be
 * seen before anyone is, and before the JD is published. Each item carries its source and each bucket
 * its mix, the two things that say whether the JD's own questions are being used.
 */

export function TryAPick({ jd }: { jd: JDDetail }) {
  const questions = useJDQuestions(jd.jd.id);
  const preview = useJDPreview(jd.jd.id);
  const draft = useDraft();
  const [years, setYears] = useState("");
  const buckets = byBucket(questions.data?.buckets ?? [], (b) => b.bucket).map((b) => b.bucket);
  const counts = countsFor(buckets, draft.questionCounts(jd.jd.question_counts ?? null));

  return (
    <Panel className="min-w-0 flex-[999_1_520px] overflow-hidden">
      <PanelHeader
        title="Try a pick"
        actions={
          <span className="text-bank-muted text-xs">
            The same call ipp-platform makes · works before publishing
          </span>
        }
      />
      <div className="border-bank-border flex flex-wrap items-end gap-3 border-b px-4 py-3">
        <div>
          <label htmlFor="tp-years" className="mb-1 block text-[12.5px] font-medium">
            Member's experience <span className="text-bank-muted font-normal">(optional)</span>
          </label>
          <div className="flex items-center gap-2">
            <Input
              id="tp-years"
              type="number"
              min={0}
              max={60}
              className="w-24"
              placeholder="—"
              value={years}
              onChange={(e) => setYears(e.target.value)}
            />
            <span className="text-bank-muted text-[13px]">years</span>
          </div>
        </div>
        <p className="text-bank-muted min-w-0 flex-[1_1_200px] pb-1.5 text-[12.5px]">
          Using {buckets.map((b) => `${counts[b] ?? 0} ${bucketLabel(b).toLowerCase()}`).join(" · ")}, from
          Interview mix. The member's experience only shapes this preview.
        </p>
        <Button
          variant="primary"
          disabled={preview.isPending || buckets.length === 0}
          onClick={() =>
            preview.mutate({ buckets: counts, experience_years: years.trim() === "" ? null : Number(years) })
          }
        >
          {preview.data ? (
            <RefreshCw className="size-4" aria-hidden="true" />
          ) : (
            <Shuffle className="size-4" aria-hidden="true" />
          )}
          {preview.data ? "Pick again" : "Pick"}
        </Button>
      </div>
      <ErrorNote error={preview.error} />
      {!preview.data ? (
        <Empty>Press Pick to see the questions a member would be given with these numbers.</Empty>
      ) : (
        <>
          {byBucket(preview.data.buckets, (b) => b.bucket).map((b) => (
            <section key={b.bucket}>
              <div className="border-bank-border bg-bank-surface-2 flex flex-wrap items-center gap-x-2.5 gap-y-1 border-y px-4 py-2">
                <h3 className="text-[13.5px] font-semibold">{bucketLabel(b.bucket)}</h3>
                <span className="text-bank-muted font-mono text-[11.5px]">
                  {b.returned} of {b.requested}
                </span>
                <span className="ml-auto">{b.mix && <MixLine mix={b.mix} />}</span>
              </div>
              {b.relaxed && (
                <Callout className="mx-4 my-2">
                  Widened: the bank couldn't fill this from the JD's stacks at this difficulty, so it looked
                  wider.
                </Callout>
              )}
              <ol>
                {b.items.map((it) => (
                  <li
                    key={it.question.question_id}
                    className="border-bank-surface-3 flex items-start gap-3 border-t px-4 py-2 first:border-t-0"
                  >
                    <span className="text-bank-muted w-6 shrink-0 font-mono text-xs">
                      {it.sequence_number}
                    </span>
                    <div className="min-w-0 flex-1">
                      <p className="text-[13.5px]">{it.question.question_text}</p>
                      <p className="text-bank-muted font-mono text-[11px]">
                        #{it.question.question_id} · {it.question.stack_path ?? "—"}
                        {it.question.difficulty_score ? ` · difficulty ${it.question.difficulty_score}` : ""}
                      </p>
                    </div>
                    {it.source && <SourceChip source={it.source} />}
                  </li>
                ))}
              </ol>
            </section>
          ))}
          <p className="text-bank-muted border-bank-border border-t px-4 py-2 text-xs">
            {preview.data.buckets.some((b) => b.relaxed)
              ? "Some buckets were widened."
              : "Nothing was widened."}{" "}
            Follow-up questions aren't shown. Pick again draws another set under the same rules.
          </p>
        </>
      )}
    </Panel>
  );
}
